import { useState, useRef, useEffect, useCallback } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../../lib/api'
import { useAuth } from '../../context/AuthContext'
import { Gamepad2, Star, Clock, Trophy, ArrowLeft, Zap, Users } from 'lucide-react'
import { validateGameMessage, createErrorResponse, type ValidationError } from '../../lib/gameMessageValidation'

/** Games that use React pages instead of iframes */
const REACT_GAMES = new Set(['fact-strike'])

interface GameDefinition {
  id: string
  name: string
  description: string
  icon: string
  skills: string[]
  grade_range: { min: number; max: number }
  difficulty: string
  modes: string[]
  typical_duration_minutes: number
  is_featured: boolean
  times_played: number
  category?: string
  entry_point: string
  player_stats?: {
    times_played: number
    high_score: number
    best_accuracy: number
    total_xp_earned: number
    last_played_at: string | null
  }
}

interface GameListData {
  games: GameDefinition[]
  total: number
}

interface GameSession {
  session_id: number
  game_id: string
  mode: string
  player: {
    id: number
    student_id: number
    name: string
    level: number
    title: string
  }
  game_config: {
    xp_per_correct: number
    xp_completion_bonus: number
  }
}

interface GameEndResult {
  session_id: number
  game_id: string
  final_score: number
  questions_total: number
  questions_correct: number
  accuracy: number
  duration_seconds: number
  xp_earned: number
  player_total_xp: number
  player_level: number
  player_title: string
}

interface LinkedStudent {
  id: number
  name: string
}

export default function GameArcade() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const isParent = user?.user_type === 'parent'
  const [activeGame, setActiveGame] = useState<string | null>(null)
  const [autoPlayHandled, setAutoPlayHandled] = useState(false)
  const [activeSession, setActiveSession] = useState<GameSession | null>(null)
  const [gameResult, setGameResult] = useState<GameEndResult | null>(null)
  const [pendingInitRequest, setPendingInitRequest] = useState<boolean>(false)
  const [iframeError, setIframeError] = useState<string | null>(null)
  const [playAsStudentId, setPlayAsStudentId] = useState<number | null>(null)
  const [iframeCacheBust, setIframeCacheBust] = useState<number>(Date.now())
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const queryClient = useQueryClient()

  // Fetch linked students for parent student picker
  const { data: studentsData } = useQuery<{ students: LinkedStudent[] }>({
    queryKey: ['parent-students'],
    queryFn: async () => {
      const response = await api.get('/api/parent/students')
      return response.data
    },
    enabled: isParent,
  })

  const linkedStudents = studentsData?.students || []
  const playingAsStudent = linkedStudents.find(s => s.id === playAsStudentId)

  // Auto-select first linked student when parent loads
  useEffect(() => {
    if (isParent && linkedStudents.length > 0 && playAsStudentId === null) {
      setPlayAsStudentId(linkedStudents[0].id)
    }
  }, [isParent, linkedStudents, playAsStudentId])

  const { data, isLoading } = useQuery<GameListData>({
    queryKey: ['games'],
    queryFn: async () => {
      const response = await api.get('/api/games')
      return response.data
    },
  })

  const startGameMutation = useMutation({
    mutationFn: async (gameId: string) => {
      const response = await api.post(`/api/games/${gameId}/start`, {
        mode: 'solo',
        ...(isParent && playAsStudentId
          ? { play_as_student_id: playAsStudentId }
          : {}),
      })
      return response.data as GameSession
    },
    onSuccess: (session) => {
      setActiveSession(session)
    },
  })

  const recordEventMutation = useMutation({
    mutationFn: async ({ sessionId, event }: { sessionId: number; event: any }) => {
      const response = await api.post(`/api/games/session/${sessionId}/event`, event)
      return response.data
    },
  })

  const endGameMutation = useMutation({
    mutationFn: async ({ sessionId, data }: { sessionId: number; data: any }) => {
      const response = await api.post(`/api/games/session/${sessionId}/end`, data)
      return response.data as GameEndResult
    },
    onSuccess: (result) => {
      setGameResult(result)
      queryClient.invalidateQueries({ queryKey: ['games'] })
      queryClient.invalidateQueries({ queryKey: ['student-dashboard'] })
    },
  })

  // Handle content set requests from game
  const handleContentSetsRequest = useCallback(async (requestId: number, gameId: string) => {
    try {
      const response = await api.get(`/api/games/${gameId}/content-sets`)
      iframeRef.current?.contentWindow?.postMessage({
        source: 'quest-parent',
        type: 'content:response',
        requestId,
        contentSets: response.data.contentSets || [],
      }, window.location.origin)
    } catch (err) {
      console.error('Failed to fetch content sets:', err)
      iframeRef.current?.contentWindow?.postMessage({
        source: 'quest-parent',
        type: 'error',
        requestId,
        message: 'Failed to load content sets',
      }, window.location.origin)
    }
  }, [])

  // Handle content items request from game
  const handleContentItemsRequest = useCallback(async (
    requestId: number,
    sessionId: number,
    options: { contentSetId: number; count?: number; includeNew?: boolean; newItemLimit?: number }
  ) => {
    try {
      const response = await api.post(`/api/games/session/${sessionId}/content`, options)
      iframeRef.current?.contentWindow?.postMessage({
        source: 'quest-parent',
        type: 'content:response',
        requestId,
        items: response.data.items || [],
        stats: response.data.stats || {},
      }, window.location.origin)
    } catch (err) {
      console.error('Failed to fetch content items:', err)
      iframeRef.current?.contentWindow?.postMessage({
        source: 'quest-parent',
        type: 'error',
        requestId,
        message: 'Failed to load content items',
      }, window.location.origin)
    }
  }, [])

  // Handle item attempt from game
  const handleItemAttempt = useCallback(async (
    sessionId: number,
    attemptData: { itemId: number; studentAnswer: string; isCorrect: boolean; responseTimeMs: number }
  ) => {
    try {
      const response = await api.post(`/api/games/session/${sessionId}/item-attempt`, attemptData)
      // Send mastery update back to game
      if (response.data) {
        iframeRef.current?.contentWindow?.postMessage({
          source: 'quest-parent',
          type: 'mastery:update',
          update: response.data,
        }, window.location.origin)
      }
    } catch (err) {
      console.error('Failed to record item attempt:', err)
    }
  }, [])

  /**
   * Sends an error response back to the game iframe
   */
  const sendErrorToGame = useCallback((error: ValidationError, requestId?: number) => {
    if (iframeRef.current?.contentWindow) {
      const errorResponse = createErrorResponse(error, requestId)
      iframeRef.current.contentWindow.postMessage(errorResponse, window.location.origin)
      console.warn('[GameArcade] Validation error:', error.message)
    }
  }, [])

  // Handle messages from game iframe
  const handleGameMessage = useCallback((event: MessageEvent) => {
    // Validate origin - only accept messages from our own origin
    if (event.origin !== window.location.origin) return

    const data = event.data
    if (!data || data.source !== 'quest-game') return

    // Validate message structure
    const validation = validateGameMessage(
      data,
      activeGame || undefined,
      activeSession?.session_id
    )

    if (!validation.valid && validation.error) {
      // Send structured error response back to game
      sendErrorToGame(validation.error, data?.requestId)
      return
    }

    const eventType = data.type

    if (eventType === 'init:request') {
      if (!activeSession) {
        // Session not ready yet - mark as pending
        setPendingInitRequest(true)
        return
      }
      // Send init response to game
      iframeRef.current?.contentWindow?.postMessage({
        source: 'quest-parent',
        type: 'init:response',
        sessionId: activeSession.session_id,
        player: activeSession.player,
        gameConfig: activeSession.game_config,
      }, window.location.origin)
    } else if (eventType === 'content:sets') {
      // Handle content sets request - can happen before session is active
      if (activeGame) {
        handleContentSetsRequest(data.requestId, activeGame)
      }
    } else if (!activeSession) {
      // Other events require active session
      return
    } else if (eventType === 'content:request') {
      // Handle content items request
      handleContentItemsRequest(data.requestId, activeSession.session_id, data.data || {})
    } else if (eventType === 'item:attempted') {
      // Handle item attempt for mastery tracking
      handleItemAttempt(activeSession.session_id, data.data || {})
    } else if (eventType === 'game:end') {
      // End game session
      endGameMutation.mutate({
        sessionId: activeSession.session_id,
        data: {
          finalScore: data.data?.finalScore || 0,
          stats: data.data?.stats || {},
        },
      })
    } else {
      // Forward other events to backend
      recordEventMutation.mutate({
        sessionId: activeSession.session_id,
        event: data,
      })
    }
  }, [activeSession, activeGame, endGameMutation, recordEventMutation, handleContentSetsRequest, handleContentItemsRequest, handleItemAttempt, sendErrorToGame])

  useEffect(() => {
    window.addEventListener('message', handleGameMessage)
    return () => window.removeEventListener('message', handleGameMessage)
  }, [handleGameMessage])

  // End session on tab close / navigation away
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (activeSession && !gameResult) {
        const url = `/api/games/session/${activeSession.session_id}/abandon`
        navigator.sendBeacon(url, JSON.stringify({ abandoned: true }))
      }
    }
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [activeSession, gameResult])

  // Respond to pending init request when session becomes ready
  useEffect(() => {
    if (activeSession && pendingInitRequest && iframeRef.current?.contentWindow) {
      iframeRef.current.contentWindow.postMessage({
        source: 'quest-parent',
        type: 'init:response',
        sessionId: activeSession.session_id,
        player: activeSession.player,
        gameConfig: activeSession.game_config,
      }, window.location.origin)
      setPendingInitRequest(false)
    }
  }, [activeSession, pendingInitRequest])

  const startGame = (gameId: string) => {
    // React-based games navigate to their own page
    if (REACT_GAMES.has(gameId)) {
      navigate(`/student/${gameId}`)
      return
    }
    setActiveGame(gameId)
    setGameResult(null)
    setPendingInitRequest(false)
    setIframeError(null)
    setIframeCacheBust(Date.now())
    startGameMutation.mutate(gameId)
  }

  // Auto-start game from ?play= URL param (e.g., /student/arcade?play=sequence-order)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (autoPlayHandled || !data || activeGame) return
    const playId = searchParams.get('play')
    if (playId && data.games.some(g => g.id === playId)) {
      setAutoPlayHandled(true)
      searchParams.delete('play')
      setSearchParams(searchParams, { replace: true })
      startGame(playId)
    }
  }, [data, autoPlayHandled, activeGame])

  const exitGame = () => {
    // End the backend session before clearing state
    if (activeSession && !gameResult) {
      endGameMutation.mutate({
        sessionId: activeSession.session_id,
        data: { finalScore: 0, stats: { abandoned: true } },
      })
    }
    setActiveGame(null)
    setActiveSession(null)
    setGameResult(null)
    setPendingInitRequest(false)
    setIframeError(null)
  }

  const playAgain = () => {
    if (activeGame) {
      setGameResult(null)
      setIframeCacheBust(Date.now())
      startGameMutation.mutate(activeGame)
    }
  }

  // Show game result modal
  if (gameResult) {
    return (
      <div className="space-y-6">
        <button
          onClick={exitGame}
          className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          Back to Arcade
        </button>

        <div className="card max-w-md mx-auto text-center">
          <h2 className="text-2xl font-bold mb-6">Game Complete!</h2>

          <div className="text-5xl font-bold text-quest-primary mb-4">
            {gameResult.final_score}
          </div>
          <p className="text-gray-400 mb-6">Final Score</p>

          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="bg-surface-card rounded-lg p-3">
              <div className="text-2xl font-bold text-green-400">
                {gameResult.questions_correct}
              </div>
              <div className="text-xs text-gray-400">Correct</div>
            </div>
            <div className="bg-surface-card rounded-lg p-3">
              <div className="text-2xl font-bold">
                {Math.round(gameResult.accuracy * 100)}%
              </div>
              <div className="text-xs text-gray-400">Accuracy</div>
            </div>
            <div className="bg-surface-card rounded-lg p-3">
              <div className="text-2xl font-bold">
                {gameResult.duration_seconds}s
              </div>
              <div className="text-xs text-gray-400">Time</div>
            </div>
          </div>

          <div className="bg-quest-accent/20 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-center gap-2 text-quest-accent">
              <Zap className="w-6 h-6" />
              <span className="text-2xl font-bold">+{gameResult.xp_earned} XP</span>
            </div>
            <p className="text-sm text-gray-400 mt-1">
              Level {gameResult.player_level} {gameResult.player_title}
            </p>
          </div>

          <div className="flex gap-3">
            <button
              onClick={playAgain}
              className="flex-1 btn-primary"
            >
              Play Again
            </button>
            <button
              onClick={exitGame}
              className="flex-1 btn-secondary"
            >
              Back to Arcade
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Show active game
  if (activeGame && activeSession) {
    const game = data?.games.find(g => g.id === activeGame)

    return (
      <div className="flex flex-col h-[calc(100vh-5rem)] lg:h-[calc(100vh-4rem)]">
        <div className="flex items-center justify-between mb-2">
          <button
            onClick={exitGame}
            className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            Exit Game
          </button>
          <div className="text-sm text-gray-400">
            {game?.name}
          </div>
        </div>

        {iframeError ? (
          <div className="bg-red-900/20 border border-red-500/50 rounded-xl p-8 text-center">
            <p className="text-red-400 font-medium mb-2">Failed to load game</p>
            <p className="text-gray-400 text-sm">{iframeError}</p>
            <button onClick={exitGame} className="mt-4 btn-secondary">
              Back to Arcade
            </button>
          </div>
        ) : (
          <div className="flex-1 min-h-0 relative">
            <iframe
              ref={iframeRef}
              src={`/games/${activeGame}/index.html?v=${iframeCacheBust}`}
              className="absolute inset-0 w-full h-full rounded-xl border border-gray-700/50"
              sandbox="allow-scripts allow-same-origin"
              allow="autoplay"
              title={game?.name || 'Game'}
              onError={() => setIframeError('Game file could not be loaded')}
            />
          </div>
        )}
      </div>
    )
  }

  // Show loading state for starting game
  if (startGameMutation.isPending) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin w-12 h-12 border-4 border-quest-primary border-t-transparent rounded-full mx-auto mb-4" />
          <p className="text-gray-400">Starting game...</p>
        </div>
      </div>
    )
  }

  // Show loading state
  if (isLoading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 w-48 bg-surface-card rounded"></div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-48 bg-surface-card rounded-xl"></div>
          ))}
        </div>
      </div>
    )
  }

  const featuredGames = data?.games.filter(g => g.is_featured) || []
  const allGames = data?.games || []

  // Group games by category
  const categoryOrder = ['Spelling', 'Math', 'History']
  const categoryIcons: Record<string, string> = {
    'Spelling': '📝',
    'Math': '🔢',
    'History': '🗺️',
  }
  const gamesByCategory = allGames.reduce<Record<string, GameDefinition[]>>((acc, game) => {
    const cat = game.category || 'Other'
    if (!acc[cat]) acc[cat] = []
    acc[cat].push(game)
    return acc
  }, {})
  const sortedCategories = [
    ...categoryOrder.filter(c => gamesByCategory[c]),
    ...Object.keys(gamesByCategory).filter(c => !categoryOrder.includes(c)),
  ]

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold flex items-center gap-3">
          <Gamepad2 className="w-8 h-8 text-quest-primary" />
          Game Arcade
        </h1>
        <p className="text-gray-400 mt-1">
          Play games to practice skills and earn XP
        </p>
      </div>

      {/* Parent: Student Picker */}
      {isParent && linkedStudents.length > 0 && (
        <div className="flex items-center gap-3 p-3 bg-surface-card/50 border border-gray-700/50 rounded-lg">
          <Users className="w-4 h-4 text-purple-400 flex-shrink-0" />
          <span className="text-sm text-gray-400">Playing as:</span>
          <select
            value={playAsStudentId ?? ''}
            onChange={(e) => setPlayAsStudentId(e.target.value ? Number(e.target.value) : null)}
            className="bg-gray-700 border border-gray-600 rounded-lg px-3 py-1.5 text-sm text-gray-200"
          >
            {linkedStudents.map(s => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
          {playingAsStudent && (
            <span className="text-xs text-purple-400">
              Mastery data will count for {playingAsStudent.name}
            </span>
          )}
        </div>
      )}

      {/* Featured Games */}
      {featuredGames.length > 0 && (
        <section>
          <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
            <Star className="w-5 h-5 text-yellow-500" />
            Featured
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {featuredGames.map((game) => (
              <GameCard key={game.id} game={game} onPlay={startGame} featured />
            ))}
          </div>
        </section>
      )}

      {/* Games by Category */}
      {allGames.length === 0 ? (
        <section>
          <div className="card text-center py-12 text-gray-400">
            <Gamepad2 className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No games available yet</p>
            <p className="text-sm mt-1">Check back soon for new games!</p>
          </div>
        </section>
      ) : (
        sortedCategories.map((category) => (
          <section key={category}>
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
              <span className="text-lg">{categoryIcons[category] || '🎮'}</span>
              {category}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {gamesByCategory[category].map((game) => (
                <GameCard key={game.id} game={game} onPlay={startGame} />
              ))}
            </div>
          </section>
        ))
      )}
    </div>
  )
}

interface GameCardProps {
  game: GameDefinition
  onPlay: (gameId: string) => void
  featured?: boolean
}

function GameCard({ game, onPlay, featured = false }: GameCardProps) {
  return (
    <div
      className={`card hover:border-quest-primary/50 transition-all cursor-pointer group ${
        featured ? 'border-yellow-500/30 bg-yellow-500/5' : ''
      }`}
      onClick={() => onPlay(game.id)}
    >
      <div className="flex items-start gap-4">
        <div className="text-4xl">{game.icon || '🎮'}</div>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-bold group-hover:text-quest-primary transition-colors">
              {game.name}
            </h3>
            {featured && (
              <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
            )}
          </div>
          <p className="text-sm text-gray-400 mt-1 line-clamp-2">
            {game.description}
          </p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mt-3">
        {game.skills.slice(0, 3).map((skill) => (
          <span
            key={skill}
            className="text-xs px-2 py-1 rounded-full bg-quest-primary/20 text-quest-primary"
          >
            {skill}
          </span>
        ))}
      </div>

      <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-700/50">
        <div className="flex items-center gap-4 text-sm text-gray-400">
          <span className="flex items-center gap-1">
            <Clock className="w-4 h-4" />
            {game.typical_duration_minutes || 5} min
          </span>
          <span className="flex items-center gap-1">
            <Trophy className="w-4 h-4" />
            {game.times_played || 0} plays
          </span>
        </div>

        {game.player_stats && (
          <div className="text-sm">
            <span className="text-quest-accent font-medium">
              Best: {game.player_stats.high_score}
            </span>
          </div>
        )}
      </div>

      <button
        className="w-full mt-4 btn-primary group-hover:bg-quest-primary/90"
        onClick={(e) => {
          e.stopPropagation()
          onPlay(game.id)
        }}
      >
        Play Now
      </button>
    </div>
  )
}
